import * as React from 'react';
import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '../primitives/alert';
import { Button } from '../primitives/button';
import { resolveErrorMessage } from './error-message';

/** Shown only when the thrown value carries no reason at all. */
export const UNKNOWN_ERROR_MESSAGE = 'An unknown error occurred';

export interface ErrorStateProps {
  /** Short error title */
  title?: string;
  /** Detailed error message */
  message: string | Error | unknown;
  /** Retry callback (renders a retry button if provided) */
  onRetry?: () => void;
  className?: string;
}

/**
 * Error state banner for failed data loads or mutations.
 * Composes the `Alert` primitive with an optional retry action.
 *
 * `message` is deliberately `unknown`-ish: call sites hand it the raw thrown
 * value. It is resolved through {@link resolveErrorMessage}, so an
 * `ApiRequestError` from `@tyndo/hooks`, a raw RFC-7807 ProblemDetails body and
 * a legacy `{ error: "…" }` envelope all surface the server's stated reason.
 * The generic {@link UNKNOWN_ERROR_MESSAGE} is reached only when the value
 * genuinely carries no reason — it is a last resort, never the default.
 *
 * @example
 * ```tsx
 * <ErrorState
 *   title="Failed to load work orders"
 *   message={error}
 *   onRetry={() => refetch()}
 * />
 * ```
 */
export const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(
  ({ title = 'Something went wrong', message, onRetry, className }, ref) => {
    const messageText = resolveErrorMessage(message, UNKNOWN_ERROR_MESSAGE);

    return (
      <Alert ref={ref} variant="destructive" className={className}>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p>{messageText}</p>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }
);

ErrorState.displayName = 'ErrorState';
